import Image from "next/image";
import Link from "next/link";
import type { TrailCardModel } from "@/lib/trail-view";
import { TrailDifficultyMeterCompact } from "./TrailDifficultyMeter";
import { TrailStatusChip } from "./TrailChips";

/**
 * A trail card.
 *
 * Follows the site's existing card DNA — bordered, rounded-lg, accent border
 * on hover, image on top — with one deliberate difference: it survives having
 * no photograph. No Margalla photo in the library is verifiably OF a named
 * trail, and captioning a generic hillside as "Trail 3" would be a small lie
 * repeated across the whole collection. So the no-image state is designed
 * rather than patched: a tinted panel carrying a ridge line drawn from the
 * difficulty rating itself. It reads as a decision, not a missing asset.
 *
 * Facts render only when verified, so cards in the same grid legitimately
 * carry different numbers of them. That is the point — an empty "—" would
 * suggest the figure is zero rather than unmeasured.
 */
export function TrailCard({ trail }: { trail: TrailCardModel }) {
  return (
    <Link
      href={trail.href}
      className="group flex flex-col overflow-hidden rounded-lg border border-[var(--border-subtle)] bg-[var(--surface)] no-underline transition-colors duration-200 hover:border-[color-mix(in_srgb,var(--cta)_45%,transparent)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--cta)]"
    >
      <div className="relative aspect-[16/10] overflow-hidden bg-[var(--panel)]">
        {trail.image ? (
          <Image
            src={trail.image}
            alt={trail.imageAlt}
            fill
            // Anchored high for the same reason as the hero: the trailhead
            // signage that identifies the trail lives in the top third.
            className="object-cover object-[center_25%] transition-transform duration-[600ms] ease-out group-hover:scale-105"
            sizes="(max-width:640px) 100vw, (max-width:980px) 50vw, 33vw"
          />
        ) : (
          <RidgePlaceholder level={trail.difficulty?.level ?? null} />
        )}
        {trail.showStatusFlag ? (
          <span className="absolute left-3 top-3 rounded-full bg-[var(--panel)]/90 backdrop-blur-sm">
            <TrailStatusChip status={trail.status} />
          </span>
        ) : null}
      </div>

      <div className="flex flex-1 flex-col gap-3 p-5">
        <div>
          <p className="m-0 text-[0.72rem] font-semibold tracking-[0.14em] uppercase text-[var(--muted)]">
            {trail.location}
          </p>
          <h3 className="mt-2 mb-0 font-[family-name:var(--font-display)] text-[1.35rem] leading-tight font-medium tracking-tight text-[var(--foreground)]">
            {trail.name}
          </h3>
        </div>

        <p className="m-0 text-[0.92rem] leading-relaxed text-[var(--muted)]">{trail.summary}</p>

        {trail.difficulty ? (
          <TrailDifficultyMeterCompact
            difficulty={trail.difficulty.level}
            note={trail.difficulty.note}
          />
        ) : (
          // Said plainly rather than left blank: a card with no meter next to
          // cards that have one otherwise reads as "easy".
          <p className="m-0 text-[0.78rem] font-medium text-[var(--muted)]">Difficulty not yet rated</p>
        )}

        {trail.facts.length > 0 ? (
          <dl className="m-0 flex flex-wrap gap-x-5 gap-y-1.5">
            {trail.facts.map((fact) => (
              <div key={fact.key} className="flex items-baseline gap-1.5">
                <dt className="m-0 text-[0.7rem] tracking-wide uppercase text-[var(--muted)]">
                  {fact.label}
                </dt>
                <dd className="m-0 text-[0.85rem] font-semibold tabular-nums text-[var(--foreground)]">
                  {fact.value}
                </dd>
              </div>
            ))}
          </dl>
        ) : null}

        <span className="mt-auto inline-flex items-center gap-1.5 pt-1 text-[0.82rem] font-bold tracking-wide text-[var(--cta)]">
          Explore Trail
          <svg
            viewBox="0 0 24 24"
            width="14"
            height="14"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            className="transition-transform duration-200 group-hover:translate-x-0.5"
          >
            <path d="M5 12h14M13 6l6 6-6 6" />
          </svg>
        </span>
      </div>
    </Link>
  );
}

/**
 * The no-photograph state: a ridge profile whose peak sits at the trail's
 * difficulty. Decorative — everything it encodes is also written out in the
 * card body — so it is hidden from assistive technology.
 */
function RidgePlaceholder({ level }: { level: number | null }) {
  // Five ridge silhouettes, each peaking further right and higher. An unrated
  // trail gets the flattest, most neutral profile rather than a fake peak.
  const peak = level ?? 0;
  const height = 26 + peak * 9;
  const apex = 22 + peak * 11;

  return (
    <span className="absolute inset-0 flex items-end justify-center overflow-hidden bg-[linear-gradient(180deg,var(--surface),var(--panel))]">
      <svg
        viewBox="0 0 120 60"
        preserveAspectRatio="none"
        className="h-full w-full"
        aria-hidden="true"
        focusable="false"
      >
        <path
          d={`M0 60 L${apex - 18} ${60 - height * 0.55} L${apex} ${60 - height} L${apex + 26} ${60 - height * 0.4} L78 ${60 - height * 0.62} L120 60 Z`}
          fill="color-mix(in srgb, var(--cta) 12%, transparent)"
          stroke="color-mix(in srgb, var(--cta) 34%, transparent)"
          strokeWidth="0.8"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
    </span>
  );
}
